import React, { useState, useEffect, useRef } from 'react';
import { X, Zap } from 'lucide-react';

const BANNER_DISMISSED_KEY = 'rae_sale_banner_dismissed_v2';
export const SALE_BANNER_H = 38;

const SALE_END = new Date('2026-11-01T00:00:00.000Z');

function useCountdown(target: Date) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    const d = Math.floor(diff / 86_400_000);
    const h = Math.floor((diff % 86_400_000) / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1_000);
    return { d, h, m, s, expired: diff === 0 };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

function useIsMobile() {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return mobile;
}

// ── DESKTOP: original 38px marquee strip ─────────────────────────────────────
function DesktopBanner({ countdown, glitch, dismiss }: {
  countdown: ReturnType<typeof useCountdown>;
  glitch: boolean;
  dismiss: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    type Particle = { x: number; y: number; speed: number; opacity: number; color: string; size: number };
    const colors = ['#FF2D78', '#00A6EB', '#B44FFF', '#00FFB2'];
    const particles: Particle[] = Array.from({ length: 28 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      speed: 0.18 + Math.random() * 0.32,
      opacity: 0.15 + Math.random() * 0.45,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 1 + Math.random() * 1.5,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
        p.x += p.speed;
        if (p.x > canvas.width + 4) p.x = -4;
      }
      ctx.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{
      position: 'relative', width: '100%', zIndex: 1, height: '38px', overflow: 'hidden',
      background: 'linear-gradient(90deg, #050010 0%, #0d0025 18%, #1a0040 38%, #0d001f 58%, #100028 78%, #050010 100%)',
      borderBottom: '1px solid transparent', backgroundClip: 'padding-box',
    }}>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent 0%, #B44FFF 15%, #FF2D78 35%, #00A6EB 55%, #00FFB2 70%, #B44FFF 85%, transparent 100%)', boxShadow: '0 0 8px rgba(255,45,120,0.6), 0 0 16px rgba(0,166,235,0.4)' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(180,79,255,0.4) 30%, rgba(0,166,235,0.4) 70%, transparent 100%)' }} />
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: 0, left: '-80%', width: '40%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(180,79,255,0.07), rgba(255,45,120,0.05), transparent)', animation: 'saleSweep 2.8s linear infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', padding: '0 2.5rem' }}>
        <div style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><path d="M9 1H2V13H9" stroke="#B44FFF" strokeWidth="1.2" strokeLinecap="square"/></svg>
          <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.5rem', color: 'rgba(180,79,255,0.6)', letterSpacing: '0.2em', fontWeight: 700 }}>SALE</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0.6rem', background: 'linear-gradient(135deg, rgba(255,45,120,0.18) 0%, rgba(180,79,255,0.12) 100%)', border: '1px solid rgba(255,45,120,0.5)', boxShadow: '0 0 10px rgba(255,45,120,0.25), inset 0 0 8px rgba(255,45,120,0.06)', overflow: 'hidden' }}>
            <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.72rem', fontWeight: 900, color: '#FF2D78', letterSpacing: '0.1em', textShadow: glitch ? '2px 0 #00FFB2, -2px 0 #B44FFF, 0 0 16px rgba(255,45,120,1)' : '0 0 14px rgba(255,45,120,0.9)', transform: glitch ? 'translateX(1px)' : 'none', transition: 'none' }}>⚡ 50% OFF</span>
          </div>
          <div style={{ width: '1px', height: '16px', background: 'linear-gradient(180deg, transparent, rgba(0,166,235,0.4), transparent)' }} />
          <span style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.82rem', fontWeight: 600, color: 'rgba(232,244,255,0.82)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>INTRODUCTORY OFFER — AUTO-APPLIED AT CHECKOUT</span>
          <div style={{ width: '1px', height: '16px', background: 'linear-gradient(180deg, transparent, rgba(0,166,235,0.4), transparent)' }} />
          {!countdown.expired && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', padding: '0.1rem 0.55rem', background: 'linear-gradient(135deg, rgba(0,166,235,0.1) 0%, rgba(180,79,255,0.08) 100%)', border: '1px solid rgba(0,166,235,0.4)', boxShadow: '0 0 10px rgba(0,166,235,0.2)' }}>
              <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.45rem', color: 'rgba(0,166,235,0.6)', letterSpacing: '0.12em', fontWeight: 700, marginRight: '0.25rem' }}>ENDS</span>
              {([{ v: countdown.d, label: 'D' }, { v: countdown.h, label: 'H' }, { v: countdown.m, label: 'M' }, { v: countdown.s, label: 'S' }] as { v: number; label: string }[]).map(({ v, label }, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.65rem', color: 'rgba(0,166,235,0.5)', fontWeight: 700, lineHeight: 1, margin: '0 1px' }}>:</span>}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '1.5rem' }}>
                    <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.72rem', fontWeight: 900, color: '#00A6EB', textShadow: '0 0 10px rgba(0,166,235,0.8)', lineHeight: 1, letterSpacing: '0.05em' }}>{String(v).padStart(2, '0')}</span>
                    <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.36rem', color: 'rgba(0,166,235,0.45)', letterSpacing: '0.1em', fontWeight: 700, marginTop: '1px' }}>{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
          <div style={{ width: '1px', height: '16px', background: 'linear-gradient(180deg, transparent, rgba(0,166,235,0.4), transparent)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.12rem 0.5rem', background: 'rgba(0,166,235,0.08)', border: '1px solid rgba(0,166,235,0.35)', boxShadow: '0 0 8px rgba(0,166,235,0.15)' }}>
            <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.55rem', color: '#00A6EB', letterSpacing: '0.12em', fontWeight: 700, textShadow: '0 0 8px rgba(0,166,235,0.7)' }}>+ BUNDLES 20% OFF</span>
          </div>
        </div>
        <div style={{ position: 'absolute', right: '2rem', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.5rem', color: 'rgba(0,166,235,0.5)', letterSpacing: '0.2em', fontWeight: 700 }}>LIVE</span>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><path d="M1 1H8V13H1" stroke="#00A6EB" strokeWidth="1.2" strokeLinecap="square"/></svg>
        </div>
        <button onClick={dismiss} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(232,244,255,0.3)', padding: '0.2rem', display: 'flex', alignItems: 'center', lineHeight: 1 }} onMouseEnter={e => (e.currentTarget.style.color = '#FF2D78')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(232,244,255,0.3)')} aria-label="Dismiss sale banner"><X size={12} /></button>
      </div>
      <style>{`@keyframes saleSweep { 0% { left: -40%; } 100% { left: 110%; } }`}</style>
    </div>
  );
}

// ── MOBILE: premium 2-row card ────────────────────────────────────────────────
function MobileBanner({ countdown, glitch, dismiss }: {
  countdown: ReturnType<typeof useCountdown>;
  glitch: boolean;
  dismiss: () => void;
}) {
  return (
    <div style={{
      position: 'relative', width: '100%', zIndex: 1, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0a0018 0%, #150030 40%, #0d001f 70%, #060010 100%)',
      borderBottom: '1px solid rgba(255,45,120,0.35)',
      boxShadow: '0 4px 24px rgba(255,45,120,0.12), 0 2px 8px rgba(0,0,0,0.6)',
      padding: '0.7rem 1rem 0.65rem',
      animation: 'mobileBannerSlideDown 0.45s cubic-bezier(0.23, 1, 0.32, 1) both',
    }}>
      {/* Top accent line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, #B44FFF, #FF2D78, #00A6EB, #00FFB2, #B44FFF)', backgroundSize: '200% 100%', animation: 'mobileBannerShift 3s linear infinite' }} />
      {/* Bottom glow line */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,45,120,0.5), rgba(0,166,235,0.5), transparent)' }} />
      {/* Sweep */}
      <div style={{ position: 'absolute', top: 0, left: '-60%', width: '35%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(180,79,255,0.06), transparent)', animation: 'saleSweep 3.5s linear infinite', pointerEvents: 'none' }} />

      {/* Row 1: badge + message + dismiss */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
        {/* 50% badge */}
        <div style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.25rem 0.7rem',
          background: 'linear-gradient(135deg, rgba(255,45,120,0.22), rgba(180,79,255,0.15))',
          border: '1px solid rgba(255,45,120,0.6)',
          boxShadow: '0 0 14px rgba(255,45,120,0.35), inset 0 0 10px rgba(255,45,120,0.08)',
        }}>
          <Zap size={12} color="#FF2D78" style={{ flexShrink: 0 }} />
          <span style={{
            fontFamily: 'Orbitron, monospace', fontSize: '1rem', fontWeight: 900, color: '#FF2D78',
            letterSpacing: '0.08em', lineHeight: 1,
            textShadow: glitch
              ? '2px 0 #00FFB2, -2px 0 #B44FFF, 0 0 20px rgba(255,45,120,1)'
              : '0 0 16px rgba(255,45,120,0.9), 0 0 32px rgba(255,45,120,0.4)',
            transform: glitch ? 'translateX(1px)' : 'none',
          }}>50% OFF</span>
        </div>

        {/* Message */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.82rem', fontWeight: 700, color: 'rgba(232,244,255,0.9)', letterSpacing: '0.04em', lineHeight: 1.2 }}>
            INTRODUCTORY OFFER
          </div>
          <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.55rem', color: 'rgba(0,166,235,0.7)', letterSpacing: '0.08em', marginTop: '0.1rem' }}>
            AUTO-APPLIED AT CHECKOUT
          </div>
        </div>

        {/* Dismiss */}
        <button
          onClick={dismiss}
          style={{ flexShrink: 0, background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.2)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(232,244,255,0.4)', padding: 0 }}
          onTouchStart={e => (e.currentTarget.style.color = '#FF2D78')}
          onTouchEnd={e => (e.currentTarget.style.color = 'rgba(232,244,255,0.4)')}
          aria-label="Dismiss sale banner"
        >
          <X size={11} />
        </button>
      </div>

      {/* Row 2: countdown + bundles badge — grid so neither can overflow */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', width: '100%' }}>
        {/* Countdown */}
        {!countdown.expired && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            padding: '0.28rem 0.5rem',
            background: 'linear-gradient(135deg, rgba(0,166,235,0.1), rgba(180,79,255,0.07))',
            border: '1px solid rgba(0,166,235,0.35)',
            boxShadow: '0 0 12px rgba(0,166,235,0.2)',
            minWidth: 0,
          }}>
            <span style={{ fontFamily: 'Orbitron, monospace', fontSize: 'clamp(0.34rem, 1.6vw, 0.45rem)', color: 'rgba(0,166,235,0.65)', letterSpacing: '0.08em', fontWeight: 700, flexShrink: 0 }}>ENDS</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.1rem', flex: 1, justifyContent: 'center' }}>
              {([{ v: countdown.d, label: 'D' }, { v: countdown.h, label: 'H' }, { v: countdown.m, label: 'M' }, { v: countdown.s, label: 'S' }] as { v: number; label: string }[]).map(({ v, label }, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span style={{ fontFamily: 'Orbitron, monospace', fontSize: 'clamp(0.62rem, 3vw, 0.85rem)', color: 'rgba(0,166,235,0.4)', fontWeight: 700, lineHeight: 1, marginBottom: '0.5rem' }}>:</span>}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'clamp(1.35rem, 6vw, 1.8rem)' }}>
                    <span style={{ fontFamily: 'Orbitron, monospace', fontSize: 'clamp(0.72rem, 3.5vw, 0.95rem)', fontWeight: 900, color: '#00A6EB', textShadow: '0 0 12px rgba(0,166,235,0.9)', lineHeight: 1, letterSpacing: '0.04em' }}>{String(v).padStart(2, '0')}</span>
                    <span style={{ fontFamily: 'Orbitron, monospace', fontSize: '0.36rem', color: 'rgba(0,166,235,0.5)', letterSpacing: '0.1em', fontWeight: 700, marginTop: '2px' }}>{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Bundles badge — auto width, never overflows */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0.28rem 0.5rem',
          background: 'rgba(0,166,235,0.08)',
          border: '1px solid rgba(0,166,235,0.35)',
          boxShadow: '0 0 10px rgba(0,166,235,0.15)',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ fontFamily: 'Orbitron, monospace', fontSize: 'clamp(0.36rem, 1.8vw, 0.48rem)', color: '#00A6EB', letterSpacing: '0.05em', fontWeight: 700, textShadow: '0 0 8px rgba(0,166,235,0.7)' }}>◈ BUNDLES 20% OFF</span>
        </div>
      </div>

      <style>{`
        @keyframes saleSweep { 0% { left: -40%; } 100% { left: 110%; } }
        @keyframes mobileBannerShift { 0% { background-position: 0% 0; } 100% { background-position: 200% 0; } }
        @keyframes mobileBannerSlideDown { 0% { transform: translateY(-110%); opacity: 0; } 60% { opacity: 1; } 100% { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

export default function SaleBanner() {
  const [visible, setVisible] = useState(false);
  const [glitch, setGlitch] = useState(false);
  const isMobile = useIsMobile();
  const countdown = useCountdown(SALE_END);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial visibility: show unless dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissed) setVisible(true);
  }, []);

  // 30-minute inactivity re-show
  useEffect(() => {
    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        sessionStorage.removeItem(BANNER_DISMISSED_KEY);
        setVisible(true);
      }, INACTIVITY_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, []);

  // Glitch pulse
  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 180);
    }, 4200);
    return () => clearInterval(interval);
  }, [visible]);

  const dismiss = () => {
    sessionStorage.setItem(BANNER_DISMISSED_KEY, '1');
    setVisible(false);
  };

  // Reserve layout space to prevent CLS when banner is hidden
  if (!visible) {
    return (
      <div
        aria-hidden="true"
        style={{
          height: isMobile ? 0 : SALE_BANNER_H,
          overflow: 'hidden',
          transition: 'height 0.3s ease',
        }}
      />
    );
  }

  return isMobile
    ? <MobileBanner countdown={countdown} glitch={glitch} dismiss={dismiss} />
    : <DesktopBanner countdown={countdown} glitch={glitch} dismiss={dismiss} />;
}
